import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ReceiptText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Section } from "../components/ui/section";
import { ExpenseAmount } from "../components/ExpenseAmount";
import { useAuth } from "../auth/AuthContext";
import {
  useCancelMyJoinRequest,
  useGroup,
  useInfinitePublicGroupInviteExpenses,
  useMyJoinRequests,
  usePublicGroupInvite,
  useSubmitJoinRequest,
} from "../api/hooks";
import { formatRole } from "../lib/groupRole";
import type { JoinRequest, PublicExpense, PublicGroupView } from "../api/types";

type ClaimableRole = "MEMBER" | "VIEWER";
type Mode = "TAKEOVER" | "NEW";

/**
 * Public-or-authenticated invite preview at `/groups/:groupId/join`.
 *
 * - Anonymous: shows the public read-only group view (or "this invite is
 *   disabled" / "not found"), a read-only list of the group's expenses, and a
 *   "Sign in to join" CTA. Guests can browse but not edit anything.
 * - Authenticated non-member: same preview plus a "Request to join" form.
 * - Authenticated member: redirects to `/groups/:groupId`.
 */
export function GroupJoinPage(): ReactNode {
  const { groupId } = useParams<{ groupId: string }>();
  const { status, login } = useAuth();
  const publicQ = usePublicGroupInvite(groupId);
  const myGroupQ = useGroup(status === "authenticated" ? groupId : undefined);
  // Public visitors hit this page without a session, so the authenticated
  // /users/me/join-requests fetch must be gated to avoid 401 + retry noise.
  const myReqsQ = useMyJoinRequests({ enabled: status === "authenticated" });

  if (publicQ.isLoading) {
    return <Skeleton className="m-8 h-64 max-w-xl" />;
  }
  if (!publicQ.data) {
    return (
      <div className="grid min-h-screen place-items-center px-5 text-on-surface-variant">
        This invite is unavailable. Ask the group's owner to share a fresh link.
      </div>
    );
  }

  // Already a member of this group — bail to the authenticated detail page.
  if (status === "authenticated" && myGroupQ.data) {
    return <Navigate to={`/groups/${publicQ.data.id}`} replace />;
  }

  // Defence-in-depth status filter: the server-side GSI is sparse on
  // PENDING, but a stale cache or future change to listMineForUser semantics
  // could surface decided rows. Filtering here keeps the UI honest.
  const myPending = myReqsQ.data?.items.find(
    (r) => r.groupId === publicQ.data!.id && r.status === "PENDING",
  );

  return (
    <div className="mx-auto max-w-2xl space-y-stack-lg px-6 py-12 sm:px-8">
      <PublicHeader view={publicQ.data} />

      {status === "loading" ? null : status === "anonymous" ? (
        <AnonymousJoinCta
          onLogin={() => void login(`/groups/${publicQ.data!.id}/join`)}
        />
      ) : myPending ? (
        <PendingRequestCard request={myPending} />
      ) : (
        <RequestToJoinCard view={publicQ.data} />
      )}

      <PublicParticipantsList view={publicQ.data} />

      <PublicExpensesList view={publicQ.data} />

      <div>
        <Link to="/" className="text-label-md text-primary hover:underline">
          ← Back to Tailspend
        </Link>
      </div>
    </div>
  );
}

function PublicHeader({ view }: { view: PublicGroupView }): ReactNode {
  return (
    <div className="space-y-1">
      <p className="text-label-sm text-on-surface-variant">You're invited to</p>
      <h1 className="text-headline-lg">{view.label}</h1>
      <p className="text-body-md text-on-surface-variant">
        {view.participants.length} participants · {view.currency}
      </p>
    </div>
  );
}

function PublicParticipantsList({
  view,
}: {
  view: PublicGroupView;
}): ReactNode {
  return (
    <div className="space-y-3">
      <h2 className="text-headline-md">Participants</h2>
      {view.participants.length === 0 ? (
        <p className="text-body-md text-on-surface-variant">
          This group is empty.
        </p>
      ) : (
        <Card tone="ghost">
          <CardContent className="space-y-2 p-6">
            {view.participants.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between border-b border-outline-variant/30 py-2 last:border-b-0"
              >
                <span>{p.displayName}</span>
                <span className="text-label-sm text-on-surface-variant">
                  {formatRole(p.role)}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Read-only expense list for guests. Reuses the public invite-expenses
 * endpoint (gated server-side on the invite-enabled flag) and walks pages on
 * demand. Rows link to the public expense detail view; nothing here mutates.
 */
function PublicExpensesList({ view }: { view: PublicGroupView }): ReactNode {
  const q = useInfinitePublicGroupInviteExpenses(view.id);
  const expenses = q.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Section title="Expenses" contentClassName="space-y-stack-sm">
      {q.isLoading ? (
        <Skeleton className="h-32 rounded-md" />
      ) : expenses.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
            <ReceiptText className="size-6" />
          </span>
          <p className="text-body-md text-on-surface-variant">
            No expenses in this group yet.
          </p>
        </div>
      ) : (
        <>
          {expenses.map((e) => (
            <PublicExpenseRow key={e.id} group={view} expense={e} />
          ))}
          {q.hasNextPage ? (
            <div className="pt-2 text-center">
              <Button
                variant="ghost"
                onClick={() => void q.fetchNextPage()}
                disabled={q.isFetchingNextPage}
              >
                {q.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

function PublicExpenseRow({
  group,
  expense,
}: {
  group: PublicGroupView;
  expense: PublicExpense;
}): ReactNode {
  const total = expense.items.reduce((sum, item) => sum + item.cost, 0);
  return (
    <Link
      to={`/groups/${group.id}/join/expenses/${expense.id}`}
      className="flex items-center justify-between gap-4 rounded-md border border-outline-variant/60 p-4 transition-colors hover:border-primary/50 hover:bg-surface-container/40"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-primary-fixed text-primary">
          <ReceiptText className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="break-words text-body-lg">{expense.label}</p>
          <p className="text-label-md text-on-surface-variant">
            {new Date(`${expense.expenseDate}T12:00:00`).toLocaleDateString()}
          </p>
        </div>
      </div>
      <ExpenseAmount
        minor={total}
        currency={expense.currency}
        groupCharge={expense.groupCharge}
        groupCurrency={group.currency}
        className="shrink-0 text-headline-md"
      />
    </Link>
  );
}

function AnonymousJoinCta({ onLogin }: { onLogin: () => void }): ReactNode {
  return (
    <Card tone="primary">
      <CardContent className="flex flex-col items-start gap-4 p-6">
        <p className="text-body-lg">
          Sign in to ask the owner to add you to this group.
        </p>
        <Button variant="secondary" onClick={onLogin}>
          Sign in to join
        </Button>
      </CardContent>
    </Card>
  );
}

function PendingRequestCard({ request }: { request: JoinRequest }): ReactNode {
  const cancel = useCancelMyJoinRequest(request.groupId);
  return (
    <Card tone="ghost">
      <CardContent className="flex flex-col items-start gap-4 p-6">
        <p className="text-body-lg">
          Your request is pending — an owner will review it.
        </p>
        <Button
          variant="ghost"
          onClick={() => void cancel.mutateAsync()}
          disabled={cancel.isPending}
        >
          Cancel my request
        </Button>
        {cancel.error ? (
          <p className="text-label-md text-error">
            Couldn't cancel — it may have already been decided.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function RequestToJoinCard({ view }: { view: PublicGroupView }): ReactNode {
  const submit = useSubmitJoinRequest(view.id);
  const guests = useMemo(
    () => view.participants.filter((p) => p.type === "GUEST"),
    [view.participants],
  );
  const [mode, setMode] = useState<Mode>(
    guests.length > 0 ? "TAKEOVER" : "NEW",
  );
  // Track the user's pick separately from "the actual selected id" so the
  // dropdown stays driven by render-time selection logic rather than a
  // setState-in-effect cascade. If the picked id is no longer among the
  // current guests (e.g. the page revalidates and the GUEST is gone), we
  // fall back to the first guest in the list at render time.
  const [pickedParticipantId, setPickedParticipantId] = useState<string>(
    guests[0]?.id ?? "",
  );
  const targetParticipantId = guests.find((g) => g.id === pickedParticipantId)
    ? pickedParticipantId
    : (guests[0]?.id ?? "");
  const [displayName, setDisplayName] = useState<string>("");
  const [proposedRole, setProposedRole] = useState<ClaimableRole>("MEMBER");

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (mode === "TAKEOVER" && !targetParticipantId) return;
    if (mode === "NEW" && !displayName.trim()) return;
    await submit.mutateAsync(
      mode === "TAKEOVER"
        ? { mode: "TAKEOVER", targetParticipantId, proposedRole }
        : { mode: "NEW", displayName: displayName.trim(), proposedRole },
    );
  }

  return (
    <Card tone="primary">
      <CardContent className="space-y-4 p-6">
        <p className="text-body-lg">Request to join this group</p>
        <form className="space-y-4" onSubmit={onSubmit}>
          <fieldset className="space-y-2">
            <Label>How would you like to join?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={mode === "TAKEOVER" ? "default" : "ghost"}
                disabled={guests.length === 0}
                onClick={() => setMode("TAKEOVER")}
              >
                Take over an existing guest
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === "NEW" ? "default" : "ghost"}
                onClick={() => setMode("NEW")}
              >
                Join as a new person
              </Button>
            </div>
          </fieldset>

          {mode === "TAKEOVER" ? (
            <div className="space-y-2">
              <Label htmlFor="join-target">Existing guest</Label>
              <select
                id="join-target"
                value={targetParticipantId}
                onChange={(e) => setPickedParticipantId(e.target.value)}
                className="flex h-11 w-full rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {guests.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.displayName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="join-display-name">Your display name</Label>
              <Input
                id="join-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Alex"
                maxLength={64}
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="join-role">Proposed role</Label>
            <select
              id="join-role"
              value={proposedRole}
              onChange={(e) => setProposedRole(e.target.value as ClaimableRole)}
              className="flex h-11 w-full rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="MEMBER">{formatRole("MEMBER")}</option>
              <option value="VIEWER">{formatRole("VIEWER")}</option>
            </select>
            <p className="text-label-sm text-on-surface-variant">
              The owner picks the final role on approve.
            </p>
          </div>

          {submit.error ? (
            <p className="text-label-md text-error">
              Couldn't submit your request. You may already have a pending one
              for this group.
            </p>
          ) : null}

          <Button type="submit" variant="secondary" disabled={submit.isPending}>
            {submit.isPending ? "Submitting…" : "Submit request"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
